package fr.frux.feature.map

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Refresh
import androidx.compose.material.icons.outlined.Visibility
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_filtered_empty_anywhere
import fr.frux.feature.map.resources.map_filtered_empty_generic
import fr.frux.feature.map.resources.map_filtered_empty_season
import fr.frux.feature.map.resources.map_filtered_empty_show_nearest
import fr.frux.feature.map.resources.map_filtered_empty_species
import fr.frux.feature.map.resources.map_location_notice_action_allow
import fr.frux.feature.map.resources.map_location_notice_action_settings
import fr.frux.feature.map.resources.map_location_notice_dismiss
import fr.frux.feature.map.resources.map_location_notice_permanently_denied_settings
import fr.frux.feature.map.resources.map_location_notice_permanently_denied_web
import fr.frux.feature.map.resources.map_location_notice_permission_denied_default
import fr.frux.feature.map.resources.map_location_notice_permission_denied_explain
import fr.frux.feature.map.resources.map_location_notice_unavailable
import fr.frux.feature.map.resources.map_never_seen_invite
import fr.frux.feature.map.resources.map_never_seen_invite_dismiss
import fr.frux.feature.map.resources.map_populated_hint_dismiss
import fr.frux.feature.map.resources.map_populated_hint_text
import fr.frux.feature.map.resources.map_search_area_idle
import fr.frux.feature.map.resources.map_search_area_searching
import fr.frux.feature.map.resources.map_stale_cache
import fr.frux.feature.map.resources.map_truncated_batch
import org.jetbrains.compose.resources.stringResource

/*
 * Bandeaux d'info et pastille d'action flottant sous la barre de recherche de MapScreen (#163) :
 * extraits pour degager le fichier principal des composables qui n'orchestrent pas le layout.
 */

/**
 * Pastille « Rechercher dans cette zone » : le seul chemin qui requete apres un deplacement.
 *
 * Reste affichee pendant la recherche, desactivee, avec son indicateur : la faire disparaitre au
 * tap laisserait l'utilisateur sans preuve que son geste a porte — il retaperait, comme il le
 * faisait sur « me localiser » avant #128.
 */
@Composable
internal fun SearchAreaPill(
    isSearching: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        enabled = !isSearching,
        shape = CircleShape,
        colors =
            ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.surface,
                contentColor = MaterialTheme.colorScheme.primary,
                disabledContainerColor = MaterialTheme.colorScheme.surface,
                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant,
            ),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 4.dp),
        modifier = modifier.heightIn(min = 48.dp),
    ) {
        if (isSearching) {
            CircularProgressIndicator(
                strokeWidth = 2.dp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(18.dp),
            )
        } else {
            Icon(
                imageVector = Icons.Outlined.Refresh,
                contentDescription = null,
                modifier = Modifier.size(18.dp),
            )
        }
        Spacer(modifier = Modifier.width(FruxSpacing.sm))
        Text(
            text =
                if (isSearching) {
                    stringResource(Res.string.map_search_area_searching)
                } else {
                    stringResource(Res.string.map_search_area_idle)
                },
            style = MaterialTheme.typography.labelLarge,
        )
    }
}

/**
 * Bandeau discret « lot tronque » (#155) : le plafond client ([MAP_TREE_LIMIT]) peut couper le lot
 * bien avant que le total serveur soit epuise, et rien ne le disait jusqu'ici — l'utilisateur
 * voyait une carte incomplete sans le savoir. Rendu en pastille passive (pas de bouton : c'est un
 * constat, l'action pour en voir plus reste la pastille « Rechercher dans cette zone » une fois
 * zoome, cf. [ZOOM_IN_ARM_FACTOR]).
 */
@Composable
internal fun TruncatedBatchBanner(
    shown: Int,
    total: Int,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
        shadowElevation = 2.dp,
    ) {
        Text(
            text = stringResource(Res.string.map_truncated_batch, formatThousands(shown), formatThousands(total)),
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
        )
    }
}

/**
 * Zone vide sous un filtre serveur actif : dire pourquoi la carte est vide, et proposer d'elargir
 * aux arbres correspondants les plus proches quand il en existe peut-etre plus loin. Meme patron
 * visuel que [TruncatedBatchBanner] pour le constat, bouton facon [SearchAreaPill] pour l'action.
 */
@Composable
internal fun FilteredEmptyBanner(
    emptiness: FilteredNoResults,
    speciesSelected: Boolean,
    inSeasonOnly: Boolean,
    onShowNearest: () -> Unit,
    modifier: Modifier = Modifier,
) {
    EmptyNoticeBanner(
        text =
            when (emptiness) {
                FilteredNoResults.IN_AREA -> {
                    when {
                        speciesSelected -> stringResource(Res.string.map_filtered_empty_species)
                        inSeasonOnly -> stringResource(Res.string.map_filtered_empty_season)
                        else -> stringResource(Res.string.map_filtered_empty_generic)
                    }
                }

                FilteredNoResults.ANYWHERE -> {
                    stringResource(Res.string.map_filtered_empty_anywhere)
                }
            },
        showNearest = emptiness == FilteredNoResults.IN_AREA,
        onShowNearest = onShowNearest,
        modifier = modifier,
    )
}

@Composable
private fun EmptyNoticeBanner(
    text: String,
    showNearest: Boolean,
    onShowNearest: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier,
    ) {
        Surface(
            shape = MaterialTheme.shapes.extraLarge,
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
            shadowElevation = 2.dp,
            // Texte sur deux lignes : sans marge, le bandeau collerait aux bords de l'ecran.
            modifier = Modifier.padding(horizontal = FruxSpacing.md),
        ) {
            Text(
                text = text,
                style = MaterialTheme.typography.labelLarge,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
            )
        }

        if (showNearest) {
            Spacer(modifier = Modifier.height(FruxSpacing.sm))
            Button(
                onClick = onShowNearest,
                shape = CircleShape,
                colors =
                    ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        contentColor = MaterialTheme.colorScheme.primary,
                    ),
                elevation = ButtonDefaults.buttonElevation(defaultElevation = 4.dp),
                modifier = Modifier.heightIn(min = 48.dp),
            ) {
                Text(
                    text = stringResource(Res.string.map_filtered_empty_show_nearest),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    }
}

/**
 * Bandeau discret quand le lot affiche vient du cache local perime et que le rechargement reseau
 * a echoue (#62) : meme patron visuel que [TruncatedBatchBanner], pour rester cohérent avec le
 * reste des signaux non bloquants de cet écran.
 */
@Composable
internal fun StaleCacheBanner(modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier,
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
        shadowElevation = 2.dp,
    ) {
        Text(
            text = stringResource(Res.string.map_stale_cache),
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
        )
    }
}

/**
 * Pastille unique de première session en zone peuplée : signale le geste d'ajout à qui ne
 * rencontre jamais la zone vide. Même patron que [StaleCacheBanner], avec une fermeture
 * explicite : une pastille qu'on ne peut pas faire taire serait une notification.
 */
@Composable
internal fun PopulatedAreaHintBanner(
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.extraLarge,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
        shadowElevation = 2.dp,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = stringResource(Res.string.map_populated_hint_text),
                style = MaterialTheme.typography.labelLarge,
                modifier =
                    Modifier
                        .weight(1f)
                        .padding(start = FruxSpacing.md, top = FruxSpacing.sm, bottom = FruxSpacing.sm),
            )
            IconButton(onClick = onDismiss) {
                Icon(
                    imageVector = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.map_populated_hint_dismiss),
                    modifier = Modifier.size(18.dp),
                )
            }
        }
    }
}

/**
 * Bandeau expliquant pourquoi la position n'est pas disponible.
 *
 * Le refus definitif n'offre pas de bouton « reessayer » — redemander la permission n'afficherait
 * plus aucune boite de dialogue — mais un bouton « Reglages » qui ouvre la fiche systeme de l'app.
 * Sans lui, le bandeau demandait d'aller dans les reglages sans y emmener : un cul-de-sac.
 */
@Composable
internal fun LocationNoticeBanner(
    notice: LocationNotice,
    shouldExplain: Boolean,
    canOpenAppSettings: Boolean,
    onAction: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val message =
        when (notice) {
            LocationNotice.PERMISSION_DENIED -> {
                if (shouldExplain) {
                    stringResource(Res.string.map_location_notice_permission_denied_explain)
                } else {
                    stringResource(Res.string.map_location_notice_permission_denied_default)
                }
            }

            LocationNotice.PERMISSION_PERMANENTLY_DENIED -> {
                if (canOpenAppSettings) {
                    stringResource(Res.string.map_location_notice_permanently_denied_settings)
                } else {
                    // Web : les reglages de site ne sont pas ouvrables par script, le bandeau ne peut
                    // qu'indiquer le chemin (icone a gauche de la barre d'adresse dans Chrome).
                    stringResource(Res.string.map_location_notice_permanently_denied_web)
                }
            }

            LocationNotice.UNAVAILABLE -> {
                stringResource(Res.string.map_location_notice_unavailable)
            }
        }

    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.secondaryContainer,
        tonalElevation = 2.dp,
    ) {
        Row(
            modifier =
                Modifier.padding(
                    horizontal = FruxSpacing.md,
                    vertical = FruxSpacing.sm,
                ),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = message,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSecondaryContainer,
                modifier = Modifier.weight(1f),
            )
            val actionLabel =
                when (notice) {
                    LocationNotice.PERMISSION_DENIED -> {
                        stringResource(Res.string.map_location_notice_action_allow)
                    }

                    LocationNotice.PERMISSION_PERMANENTLY_DENIED -> {
                        if (canOpenAppSettings) stringResource(Res.string.map_location_notice_action_settings) else null
                    }

                    LocationNotice.UNAVAILABLE -> {
                        null
                    }
                }
            actionLabel?.let { label ->
                TextButton(onClick = onAction, modifier = Modifier.heightIn(min = 48.dp)) {
                    Text(label)
                }
            }
            TextButton(onClick = onDismiss, modifier = Modifier.heightIn(min = 48.dp)) {
                Text(stringResource(Res.string.map_location_notice_dismiss))
            }
        }
    }
}

/** Separateur de milliers en espace, lisible sans dependre d'un `NumberFormat` par plateforme. */
private fun formatThousands(value: Int): String {
    val negative = value < 0
    val digits = kotlin.math.abs(value).toString()
    val grouped =
        digits
            .reversed()
            .chunked(3)
            .joinToString(" ")
            .reversed()
    return if (negative) "-$grouped" else grouped
}

/**
 * Invitation a confirmer un arbre devant lequel personne n'est jamais passe (#396).
 *
 * Une action, pas une info, donc la forme de [SearchAreaPill] et non celle des bandeaux : le tap
 * ouvre la fiche, ou vivent l'observation de terrain et la photo. Il n'y a volontairement aucun
 * bouton de confirmation ici — un accord tape au reflexe fabrique de la fausse confiance, et
 * l'ADR 0025 retire ce geste du produit.
 *
 * La fermeture est explicite, avec sa propre cible tactile : une invitation qu'on ne peut pas
 * faire taire est une notification, et celle-ci arrive sans avoir ete demandee.
 */
@Composable
internal fun NeverSeenInvitePill(
    speciesName: String,
    onOpen: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier,
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.onSurface,
        shadowElevation = 4.dp,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.heightIn(min = 48.dp),
        ) {
            TextButton(
                onClick = onOpen,
                colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.primary),
            ) {
                Icon(
                    imageVector = Icons.Outlined.Visibility,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp),
                )
                Spacer(modifier = Modifier.width(FruxSpacing.sm))
                Text(
                    text = stringResource(Res.string.map_never_seen_invite, speciesName),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
            IconButton(onClick = onDismiss) {
                Icon(
                    imageVector = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.map_never_seen_invite_dismiss),
                    modifier = Modifier.size(18.dp),
                )
            }
        }
    }
}
